import React, { useEffect, useRef, useState } from 'react';
import AIReport from './AIReport';
import './ReportPopup.css';
// Logo replaced with futuristic text
import html2canvas from 'html2canvas';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, Table, TableRow, TableCell, ImageRun, AlignmentType, BorderStyle, WidthType, PageNumber, PageOrientation, Header, Footer } from 'docx';
import axios from 'axios';
import { OrganisationSettings } from '../../services/organisationSettingsService';

interface ReportPopupProps {
  isOpen: boolean;
  onClose: () => void;
  analysisResult: string;
  fileName?: string;
  isKPIAnalysisHighAccuracy?: boolean;
  kpiCodes?: string[];
  analysisType?: 'analysis' | 'slides';
  organizationSettings?: OrganisationSettings | null;
  userQuestion?: string;
}

/**
 * Parse Word formatting markers from AI response for Word export
 * Extracts header, footer, title page content, page break positions, and excluded sections
 */
function parseWordFormatMarkers(text: string): {
  headerContent: string | null;
  footerContent: string | null;
  titlePageContent: string | null;
  pageBreakPositions: string[];
  excludedSectionHeadings: string[];
} {
  if (!text) {
    return {
      headerContent: null,
      footerContent: null,
      titlePageContent: null,
      pageBreakPositions: [],
      excludedSectionHeadings: []
    };
  }

  const markers = {
    header: { start: '<!-- WORD_HEADER_START -->', end: '<!-- WORD_HEADER_END -->' },
    footer: { start: '<!-- WORD_FOOTER_START -->', end: '<!-- WORD_FOOTER_END -->' },
    titlePage: { start: '<!-- WORD_TITLE_PAGE_START -->', end: '<!-- WORD_TITLE_PAGE_END -->' }
  };
  
  const extractSection = (start: string, end: string): string | null => {
    const startIdx = text.indexOf(start);
    const endIdx = text.indexOf(end);
    if (startIdx !== -1 && endIdx !== -1 && endIdx > startIdx) {
      return text.substring(startIdx + start.length, endIdx).trim();
    }
    return null;
  };
  
  // Find all page break markers and extract the heading text that follows
  const pageBreakPositions: string[] = [];
  const pageBreakMarker = '<!-- PAGE_BREAK -->';
  let searchPos = 0;
  
  while ((searchPos = text.indexOf(pageBreakMarker, searchPos)) !== -1) {
    // Look for the next heading after this page break
    const remainingText = text.substring(searchPos + pageBreakMarker.length);
    const headingMatch = remainingText.match(/^[\s\S]*?(#{1,2}\s+(.+))/m);
    if (headingMatch && headingMatch[2]) {
      pageBreakPositions.push(headingMatch[2].trim());
    }
    searchPos += 1;
  }
  
  // Extract sections that should be excluded from Word export
  const excludedSectionHeadings: string[] = [];
  const excludeMarker = '<!-- EXCLUDE_FROM_WORD_START -->';
  const excludeEndMarker = '<!-- EXCLUDE_FROM_WORD_END -->';
  
  let excludeSearchPos = 0;
  while ((excludeSearchPos = text.indexOf(excludeMarker, excludeSearchPos)) !== -1) {
    const endPos = text.indexOf(excludeEndMarker, excludeSearchPos);
    if (endPos === -1) break;
    
    // Extract the excluded section content
    const excludedContent = text.substring(excludeSearchPos + excludeMarker.length, endPos);
    
    // Find all headings in this excluded section
    const headingRegex = /^#{1,4}\s+(.+)$/gm;
    let match;
    while ((match = headingRegex.exec(excludedContent)) !== null) {
      excludedSectionHeadings.push(match[1].trim().toLowerCase());
    }
    
    excludeSearchPos = endPos + excludeEndMarker.length;
  }
  
  const result = {
    headerContent: extractSection(markers.header.start, markers.header.end),
    footerContent: extractSection(markers.footer.start, markers.footer.end),
    titlePageContent: extractSection(markers.titlePage.start, markers.titlePage.end),
    pageBreakPositions,
    excludedSectionHeadings
  };
  
  console.log('📄 ReportPopup: Parsed Word formatting markers:', {
    hasHeader: !!result.headerContent,
    hasFooter: !!result.footerContent,
    hasTitlePage: !!result.titlePageContent,
    pageBreakCount: result.pageBreakPositions.length,
    pageBreakHeadings: result.pageBreakPositions,
    excludedSectionCount: result.excludedSectionHeadings.length,
    excludedSections: result.excludedSectionHeadings
  });
  
  return result;
}

const ReportPopup: React.FC<ReportPopupProps> = ({ isOpen, onClose, analysisResult, fileName = 'Financial Analysis', isKPIAnalysisHighAccuracy = false, kpiCodes = [], analysisType = 'analysis', organizationSettings, userQuestion }) => {
  const reportRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState('');
  const [exportType, setExportType] = useState<'word' | 'powerpoint' | 'jpeg' | null>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    
    // Cleanup function to remove the class when the component unmounts
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [isOpen]);

  const handleOutsideClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (modalRef.current && e.target === modalRef.current) {
      onClose();
    }
  };

  const getBase64Image = async (img: HTMLImageElement): Promise<string> => {
    // Use the natural dimensions of the image instead of the displayed dimensions
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    
    console.log(`Logo natural dimensions: ${img.naturalWidth}x${img.naturalHeight}`);
    console.log(`Logo display dimensions: ${img.width}x${img.height}`);
    
    const ctx = canvas.getContext('2d');
    if (ctx) {
      // Ensure the entire image is drawn on the canvas
      ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight);
      return canvas.toDataURL('image/png');
    }
    return '';
  };

  const handleExportWord = async () => {
    if (!reportRef.current) return;
    
    try {
      setIsExporting(true);
      setExportType('word');
      setExportProgress('Preparing export...');
      
      // Generate title page, header, and footer content directly
      const currentDate = new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
      
      // Use Lumina AI as organization name
      const organizationName = 'Lumina AI';
      
      // Generate title from user question or use default
      const generateTitle = (question?: string): string => {
        if (!question) return 'Performance Analysis Report';
        
        const q = question.toLowerCase();
        if (q.includes('top') || q.includes('best') || q.includes('highest')) {
          if (q.includes('dealer') || q.includes('location')) return 'Top Dealer Performance Analysis';
          if (q.includes('revenue')) return 'Revenue Performance Rankings';
          if (q.includes('sales')) return 'Sales Performance Rankings';
          return 'Performance Rankings Analysis';
        }
        if (q.includes('compare') || q.includes('comparison')) return 'Comparative Performance Analysis';
        if (q.includes('trend')) return 'Trend Analysis Report';
        if (q.includes('revenue')) return 'Revenue Analysis Report';
        if (q.includes('sales')) return 'Sales Performance Analysis';
        if (q.includes('customer') || q.includes('satisfaction')) return 'Customer Satisfaction Analysis';
        if (q.includes('profit')) return 'Profitability Analysis';
        if (q.includes('service')) return 'Service Department Analysis';
        
        return 'Performance Analysis Report';
      };
      
      const reportTitle = generateTitle(userQuestion);
      
      // Create title page content
      const titlePageContent = `# ${reportTitle}

## ${organizationName}

**Report Date:** ${currentDate}

${userQuestion ? `**Question:** ${userQuestion}` : ''}

---

Generated by Lumina AI`;
      
      // Load logos for header
      setExportProgress('Loading logos...');
      let luminaLogoBase64: string | null = null;
      let luminaLogoDimensions: { width: number; height: number } | null = null;
      let orgLogoBase64: string | null = null;
      let orgLogoDimensions: { width: number; height: number } | null = null;
      
      // Load Lumina AI logo
      try {
        const luminaLogoImg = document.querySelector('.report-logo-image') as HTMLImageElement;
        if (luminaLogoImg && luminaLogoImg.complete) {
          luminaLogoBase64 = await getBase64Image(luminaLogoImg);
          luminaLogoDimensions = {
            width: luminaLogoImg.naturalWidth,
            height: luminaLogoImg.naturalHeight
          };
        }
      } catch (error) {
        console.error('Error loading Lumina AI logo:', error);
      }
      
      // Load organization logo if available
      if (organizationSettings?.logoUrl) {
        try {
          const orgLogoImg = new Image();
          orgLogoImg.crossOrigin = 'anonymous';
          
          // Build full URL for the logo
          const logoUrl = organizationSettings.logoUrl.startsWith('http')
            ? organizationSettings.logoUrl
            : `${(process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/api$/, '')}${organizationSettings.logoUrl}`;
          
          await new Promise((resolve, reject) => {
            orgLogoImg.onload = resolve;
            orgLogoImg.onerror = reject;
            orgLogoImg.src = logoUrl;
          });
          orgLogoBase64 = await getBase64Image(orgLogoImg);
          orgLogoDimensions = {
            width: orgLogoImg.naturalWidth,
            height: orgLogoImg.naturalHeight
          };
        } catch (error) {
          console.error('Error loading organization logo:', error);
        }
      }
      
      // Calculate scaled dimensions maintaining aspect ratio
      const calculateScaledDimensions = (naturalWidth: number, naturalHeight: number, targetWidth: number) => {
        const aspectRatio = naturalHeight / naturalWidth;
        return {
          width: targetWidth,
          height: Math.round(targetWidth * aspectRatio)
        };
      };
      
      // Create header and footer content
      const headerContent = `${organizationName} | Report Date: ${currentDate}`;
      const footerContent = 'Trademark Lumina AI | Page'; // Page number will be appended by docx
      
      // Parse markers for excluded sections (if any)
      const {
        pageBreakPositions,
        excludedSectionHeadings
      } = parseWordFormatMarkers(analysisResult);
      
      // Prepare document children array
      const children: any[] = [];
      
      // Add title page (always generated)
      if (titlePageContent) {
        setExportProgress('Processing title page...');
        
        // Parse title page markdown and add formatted paragraphs
        const titleLines = titlePageContent.split('\n').filter(line => line.trim());
        
        for (const line of titleLines) {
          const trimmedLine = line.trim();
          
          // Main title (# heading)
          if (trimmedLine.startsWith('# ')) {
            children.push(
              new Paragraph({
                text: trimmedLine.substring(2).trim(),
                heading: HeadingLevel.HEADING_1,
                alignment: AlignmentType.CENTER,
                spacing: { before: 400, after: 200 }
              })
            );
          }
          // Subtitle (## heading)
          else if (trimmedLine.startsWith('## ')) {
            children.push(
              new Paragraph({
                text: trimmedLine.substring(3).trim(),
                heading: HeadingLevel.HEADING_2,
                alignment: AlignmentType.CENTER,
                spacing: { before: 0, after: 400 }
              })
            );
          }
          // Bold text (metadata like **Generated:**)
          else if (trimmedLine.startsWith('**') && trimmedLine.includes(':**')) {
            children.push(
              new Paragraph({
                text: trimmedLine.replace(/\*\*/g, ''),
                alignment: AlignmentType.CENTER,
                spacing: { before: 0, after: 120 }
              })
            );
          }
          // Horizontal rule (---)
          else if (trimmedLine === '---') {
            children.push(
              new Paragraph({
                text: '',
                spacing: { before: 200, after: 200 }
              })
            );
          }
          // Regular text
          else if (trimmedLine && !trimmedLine.startsWith('#')) {
            children.push(
              new Paragraph({
                text: trimmedLine,
                alignment: AlignmentType.CENTER,
                spacing: { before: 0, after: 120 }
              })
            );
          }
        }
        
        // Add page break after title page
        children.push(
          new Paragraph({
            text: '',
            pageBreakBefore: true
          })
        );
      }
      
      // Add logo if no title page was provided
      if (!titlePageContent) {
        const logoImg = reportRef.current.querySelector('.report-logo') as HTMLImageElement;
        if (logoImg) {
          setExportProgress('Processing logo...');
          const logoBase64 = await getBase64Image(logoImg);
          
          children.push(
            new Paragraph({
              children: [
                new ImageRun({
                  data: logoBase64.split(',')[1],
                  transformation: {
                    width: 450,
                    height: 80
                  },
                  type: 'png'
                })
              ],
              alignment: AlignmentType.CENTER,
              spacing: { after: 400 }
            })
          );
        }
      }

      // Get the report content
      const reportContent = reportRef.current.querySelector('.report-modal-body')?.querySelector('.report-content');
      if (!reportContent) {
        throw new Error("Could not find report content to export");
      }
      
      // Convert excluded headings array to Set for faster lookup
      const excludedSet = new Set(excludedSectionHeadings);
      console.log('📄 Word Export: Excluding sections:', Array.from(excludedSet));
      
      // Process content sections
      setExportProgress('Processing report content...');
      const contentElements = Array.from(reportContent.querySelectorAll('h1, h2, h3, h4, p, ul, ol, table, .visualization-container'));
      
      // Track if we're currently inside an excluded section
      let insideExcludedSection = false;
      let currentSectionHeading = '';
      
      for (const element of contentElements) {
        if (element instanceof HTMLElement) {
          const tagName = element.tagName.toLowerCase();
          const textContent = element.textContent?.trim() || '';
          
          // Skip empty elements
          if (!textContent && !element.classList.contains('visualization-container')) continue;

          // Process headings
          if (/^h[1-4]$/.test(tagName)) {
            // Check if this heading marks the start of an excluded section
            const headingTextLower = textContent.toLowerCase();
            
            if (excludedSet.has(headingTextLower)) {
              console.log(`📄 Word Export: Entering excluded section: "${textContent}"`);
              insideExcludedSection = true;
              currentSectionHeading = headingTextLower;
              continue; // Skip this heading
            } else if (insideExcludedSection) {
              // We've hit a new heading that's NOT excluded, so we're leaving the excluded section
              console.log(`📄 Word Export: Leaving excluded section, now at: "${textContent}"`);
              insideExcludedSection = false;
              currentSectionHeading = '';
            }
            
            const level = parseInt(tagName.charAt(1));
            const headingLevel = level === 1 ? HeadingLevel.HEADING_1 :
                               level === 2 ? HeadingLevel.HEADING_2 :
                               level === 3 ? HeadingLevel.HEADING_3 :
                               HeadingLevel.HEADING_4;
            
            // Check if this heading should have a page break before it
            const needsPageBreak = pageBreakPositions.some(breakHeading => 
              textContent.toLowerCase().includes(breakHeading.toLowerCase()) ||
              breakHeading.toLowerCase().includes(textContent.toLowerCase())
            );
            
            children.push(
              new Paragraph({
                text: textContent,
                heading: headingLevel,
                spacing: { before: 240, after: 120 },
                pageBreakBefore: needsPageBreak
              })
            );
          }
          // Skip all content if we're inside an excluded section
          else if (insideExcludedSection) {
            continue;
          }
          // Process paragraphs
          else if (tagName === 'p') {
            children.push(
              new Paragraph({
                text: textContent,
                spacing: { before: 0, after: 120 }
              })
            );
          }
          // Process lists
          else if (tagName === 'ul' || tagName === 'ol') {
            const items = Array.from(element.querySelectorAll('li'));
            
            items.forEach((item, index) => {
              children.push(
                new Paragraph({
                  text: `• ${item.textContent?.trim() || ''}`,
                  spacing: { before: 0, after: index === items.length - 1 ? 200 : 60 }
                })
              );
            });
          }
          // Process tables
          else if (tagName === 'table') {
            const rows = Array.from(element.querySelectorAll('tr'));
            const tableRows = rows.map(row => {
              const cells = Array.from(row.querySelectorAll('th, td'));
              return new TableRow({
                children: cells.map(cell => 
                  new TableCell({
                    children: [
                      new Paragraph({
                        text: cell.textContent?.trim() || '',
                        spacing: { before: 60, after: 60 }
                      })
                    ],
                    borders: {
                      top: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                      bottom: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                      left: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                      right: { style: BorderStyle.SINGLE, size: 1, color: "000000" }
                    }
                  })
                )
              });
            });
            
            children.push(
              new Table({
                rows: tableRows,
                width: {
                  size: 100,
                  type: WidthType.PERCENTAGE
                }
              })
            );
            
            // Add space after the table
            children.push(
              new Paragraph({
                text: "",
                spacing: { before: 0, after: 200 }
              })
            );
          }
          // Process charts
          else if (element.classList.contains('visualization-container')) {
            setExportProgress('Processing charts...');
            
            try {
              // Create a longer delay to ensure charts are fully rendered
              await new Promise(resolve => setTimeout(resolve, 1000));
              
              const chartContainer = element.querySelector('.chart-container');
              const canvas = element.querySelector('canvas');
              
              // Add chart title if available
              const titleElement = element.querySelector('.visualization-title') || 
                                 element.querySelector('.chart-title') ||
                                 element.querySelector('h3');
              
              if (titleElement?.textContent) {
                children.push(
                  new Paragraph({
                    text: titleElement.textContent.trim(),
                    heading: HeadingLevel.HEADING_3,
                    spacing: { before: 200, after: 120 }
                  })
                );
              }
              
              // Function to capture chart image with retries
              const captureChartImage = async (maxRetries = 3, retryDelay = 500): Promise<string | null> => {
                console.log('Attempting to capture chart:', titleElement?.textContent || 'Unnamed chart');
                
                // Try data-chart-image attribute first
                if (chartContainer) {
                  const attrImage = chartContainer.getAttribute('data-chart-image');
                  if (attrImage && attrImage.startsWith('data:image')) {
                    console.log('Using pre-rendered chart image from data-chart-image attribute');
                    return attrImage;
                  }
                }
                
                // If no canvas, we can't capture
                if (!canvas || !(canvas instanceof HTMLCanvasElement)) {
                  console.error('No canvas element found for chart');
                  return null;
                }
                
                // Try multiple times to capture the chart
                for (let attempt = 0; attempt < maxRetries; attempt++) {
                  try {
                    // If this isn't the first attempt, wait before trying again
                    if (attempt > 0) {
                      await new Promise(resolve => setTimeout(resolve, retryDelay));
                      console.log(`Retry attempt ${attempt+1} for chart capture`);
                    }
                    
                    // Ensure canvas has dimensions
                    if (canvas.width === 0 || canvas.height === 0) {
                      console.log('Canvas has zero dimensions, setting default size');
                      canvas.width = 600;
                      canvas.height = 400;
                    }
                    
                    // Log canvas state
                    console.log(`Canvas dimensions: ${canvas.width}x${canvas.height}`);
                    
                    try {
                      // First approach: use getImageData and putImageData for white background
                      const ctx = canvas.getContext('2d');
                      if (ctx) {
                        // Store original image data
                        const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                        
                        // Draw white background
                        ctx.save();
                        ctx.globalCompositeOperation = 'destination-over';
                        ctx.fillStyle = 'white';
                        ctx.fillRect(0, 0, canvas.width, canvas.height);
                        ctx.restore();
                        
                        // Capture with white background
                        const dataUrl = canvas.toDataURL('image/png', 1.0);
                        
                        // Restore original image data
                        ctx.putImageData(imgData, 0, 0);
                        
                        if (dataUrl && dataUrl.length > 100) {
                          return dataUrl;
                        }
                      }
                    } catch (err) {
                      console.warn('First capture approach failed:', err);
                      // Continue to second approach
                    }
                    
                    // Second approach: simple toDataURL
                    try {
                      const simpleDataUrl = canvas.toDataURL('image/png', 1.0);
                      if (simpleDataUrl && simpleDataUrl.length > 100) {
                        return simpleDataUrl;
                      }
                    } catch (err) {
                      console.warn('Second capture approach failed:', err);
                    }
                    
                    // Third approach: draw to a new canvas
                    try {
                      const newCanvas = document.createElement('canvas');
                      newCanvas.width = canvas.width;
                      newCanvas.height = canvas.height;
                      const newCtx = newCanvas.getContext('2d');
                      
                      if (newCtx) {
                        // Fill with white background first
                        newCtx.fillStyle = 'white';
                        newCtx.fillRect(0, 0, newCanvas.width, newCanvas.height);
                        
                        // Draw the original canvas on top
                        newCtx.drawImage(canvas, 0, 0);
                        
                        // Get data URL
                        const newDataUrl = newCanvas.toDataURL('image/png', 1.0);
                        if (newDataUrl && newDataUrl.length > 100) {
                          return newDataUrl;
                        }
                      }
                    } catch (err) {
                      console.warn('Third capture approach failed:', err);
                    }
                  } catch (err) {
                    console.warn(`Chart capture attempt ${attempt+1} failed:`, err);
                  }
                }
                
                // If we get here, all attempts failed
                console.error('All chart capture attempts failed');
                return null;
              };
              
              // Capture the chart image
              const dataUrl = await captureChartImage();
              
              // Add description if available
              const descElement = element.querySelector('.chart-description') || 
                                element.querySelector('.visualization-description');
              
              if (descElement?.textContent) {
                children.push(
                  new Paragraph({
                    text: descElement.textContent.trim(),
                    spacing: { before: 0, after: 120 },
                    style: "italic"
                  })
                );
              }
              
              if (dataUrl) {
                // Add chart image
                children.push(
                  new Paragraph({
                    children: [
                      new ImageRun({
                        data: dataUrl.split(',')[1],
                        transformation: {
                          width: 500,
                          height: 300
                        },
                        type: 'png'
                      })
                    ],
                    alignment: AlignmentType.CENTER,
                    spacing: { before: 0, after: 200 }
                  })
                );
                console.log('Chart image successfully added to Word document');
              } else {
                // No image data was captured, add placeholder text
                console.error('Failed to capture chart image - using placeholder text instead');
                children.push(
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: 'Chart visualization not available - could not capture image',
                        italics: true
                      })
                    ],
                    spacing: { before: 0, after: 200 }
                  })
                );
              }
            } catch (err) {
              console.error('Error processing chart:', err);
              children.push(
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `Chart visualization error: ${err instanceof Error ? err.message : 'Unknown error'}`,
                      italics: true
                    })
                  ],
                  spacing: { before: 0, after: 200 }
                })
              );
            }
          }
        }
      }

      // Create the final document with all content, headers, and footers
      const doc = new Document({
        sections: [
          {
            properties: {
              page: {
                size: {
                  width: 12240,   // US Letter width in twips (8.5 inches)
                  height: 15840,  // US Letter height in twips (11 inches)
                  orientation: PageOrientation.PORTRAIT
                },
                margin: {
                  top: 1440,      // 1 inch
                  right: 1440,    // 1 inch
                  bottom: 1440,   // 1 inch
                  left: 1440,     // 1 inch
                }
              }
            },
            headers: {
              default: new Header({
                children: [
                  new Table({
                    width: { size: 100, type: WidthType.PERCENTAGE },
                    borders: {
                      top: { style: BorderStyle.NONE },
                      bottom: { style: BorderStyle.NONE },
                      left: { style: BorderStyle.NONE },
                      right: { style: BorderStyle.NONE },
                      insideHorizontal: { style: BorderStyle.NONE },
                      insideVertical: { style: BorderStyle.NONE }
                    },
                    rows: [
                      new TableRow({
                        children: [
                          // Left cell - Organization logo
                          new TableCell({
                            width: { size: 25, type: WidthType.PERCENTAGE },
                            verticalAlign: 'center',
                            children: [
                              new Paragraph({
                                children: (orgLogoBase64 && orgLogoDimensions) ? [
                                  new ImageRun({
                                    data: orgLogoBase64.split(',')[1],
                                    transformation: calculateScaledDimensions(
                                      orgLogoDimensions.width,
                                      orgLogoDimensions.height,
                                      120
                                    ),
                                    type: 'png'
                                  })
                                ] : [],
                                alignment: AlignmentType.LEFT
                              })
                            ],
                            borders: {
                              top: { style: BorderStyle.NONE },
                              bottom: { style: BorderStyle.NONE },
                              left: { style: BorderStyle.NONE },
                              right: { style: BorderStyle.NONE }
                            }
                          }),
                          // Center cell - Header text
                          new TableCell({
                            width: { size: 50, type: WidthType.PERCENTAGE },
                            verticalAlign: 'center',
                            children: [
                              new Paragraph({
                                text: headerContent,
                                alignment: AlignmentType.CENTER
                              })
                            ],
                            borders: {
                              top: { style: BorderStyle.NONE },
                              bottom: { style: BorderStyle.NONE },
                              left: { style: BorderStyle.NONE },
                              right: { style: BorderStyle.NONE }
                            }
                          }),
                          // Right cell - Lumina AI logo
                          new TableCell({
                            width: { size: 25, type: WidthType.PERCENTAGE },
                            verticalAlign: 'center',
                            children: [
                              new Paragraph({
                                children: (luminaLogoBase64 && luminaLogoDimensions) ? [
                                  new ImageRun({
                                    data: luminaLogoBase64.split(',')[1],
                                    transformation: calculateScaledDimensions(
                                      luminaLogoDimensions.width,
                                      luminaLogoDimensions.height,
                                      120
                                    ),
                                    type: 'png'
                                  })
                                ] : [],
                                alignment: AlignmentType.RIGHT
                              })
                            ],
                            borders: {
                              top: { style: BorderStyle.NONE },
                              bottom: { style: BorderStyle.NONE },
                              left: { style: BorderStyle.NONE },
                              right: { style: BorderStyle.NONE }
                            }
                          })
                        ]
                      })
                    ]
                  })
                ]
              })
            },
            footers: footerContent ? {
              default: new Footer({
                children: [
                  new Paragraph({
                    children: [
                      new TextRun(footerContent + ' | Page '),
                      new TextRun({
                        children: [PageNumber.CURRENT]
                      }),
                      new TextRun(' of '),
                      new TextRun({
                        children: [PageNumber.TOTAL_PAGES]
                      })
                    ],
                    alignment: AlignmentType.CENTER
                  })
                ]
              })
            } : undefined,
            children: children
          }
        ]
      });

      // Generate and download the document
      setExportProgress('Generating document...');
      const blob = await Packer.toBlob(doc);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract report title from the AI response (first H1 heading)
      const extractReportTitle = (response: string): string => {
        // Look for the first H1 heading in markdown format
        const h1Match = response.match(/^#\s+(.+)$/m);
        if (h1Match && h1Match[1]) {
          return h1Match[1].trim();
        }
        
        // Fallback: look for lines that look like titles (all caps, title case, etc.)
        const lines = response.split('\n');
        for (const line of lines.slice(0, 10)) { // Check first 10 lines
          const trimmed = line.trim();
          if (trimmed.length > 10 && trimmed.length < 100 && 
              (trimmed.includes('Analysis') || trimmed.includes('Report') || 
               trimmed.includes('Performance') || trimmed.includes('Dashboard'))) {
            return trimmed;
          }
        }
        
        // Final fallback
        return fileName;
      };
      
      // Create filename with extracted report title and date/time
      const now = new Date();
      const dateTimeString = now.toISOString().replace(/[:.]/g, '-').slice(0, 19); // Format: YYYY-MM-DDTHH-MM-SS
      const extractedTitle = extractReportTitle(analysisResult);
      const cleanFileName = extractedTitle.replace(/[^a-z0-9\s]/gi, '').replace(/\s+/g, '_');
      link.download = `${cleanFileName}_${dateTimeString}.docx`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Reset export state
      setIsExporting(false);
      setExportProgress('');
      setExportType(null);
      
    } catch (error) {
      console.error('Error generating Word document:', error);
      setExportProgress('Error generating document');
      setTimeout(() => {
      setIsExporting(false);
        setExportProgress('');
        setExportType(null);
        alert('There was an error generating the Word document. Please try again or contact support.');
      }, 2000);
    }
  };

  const handleExportJPEG = async () => {
    if (!reportRef.current) return;
    
    try {
      setIsExporting(true);
      setExportType('jpeg' as any);
      setExportProgress('Preparing image export...');
      
      // Get the report content element
      const reportContent = reportRef.current.querySelector('.report-modal-body') as HTMLElement;
      if (!reportContent) {
        throw new Error("Could not find report content to export");
      }
      
      // Wait a moment for any animations or rendering to complete
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setExportProgress('Capturing report image...');
      
      // Configure html2canvas options for high quality
      const canvas = await html2canvas(reportContent, {
        backgroundColor: '#ffffff',
        scale: 2, // Higher resolution
        useCORS: true,
        allowTaint: true,
        logging: false,
        width: reportContent.scrollWidth,
        height: reportContent.scrollHeight,
        scrollX: 0,
        scrollY: 0,
        windowWidth: reportContent.scrollWidth,
        windowHeight: reportContent.scrollHeight,
        onclone: (clonedDoc) => {
          // Ensure all elements are visible in the clone
          const clonedContent = clonedDoc.querySelector('.report-modal-body') as HTMLElement;
          if (clonedContent) {
            clonedContent.style.overflow = 'visible';
            clonedContent.style.height = 'auto';
            clonedContent.style.maxHeight = 'none';
            
            // Make sure all charts are visible
            const charts = clonedContent.querySelectorAll('.visualization-container');
            charts.forEach(chart => {
              if (chart instanceof HTMLElement) {
                chart.style.overflow = 'visible';
                chart.style.height = 'auto';
                chart.style.pageBreakInside = 'avoid';
              }
            });
          }
        }
      });
      
      setExportProgress('Generating JPEG file...');
      
      // Convert canvas to JPEG blob with high quality
      const blob = await new Promise<Blob>((resolve) => {
        canvas.toBlob((blob) => {
          resolve(blob!);
        }, 'image/jpeg', 0.95); // 95% quality
      });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract report title for filename
      const extractReportTitle = (response: string): string => {
        const h1Match = response.match(/^#\s+(.+)$/m);
        if (h1Match && h1Match[1]) {
          return h1Match[1].trim();
        }
        
        const lines = response.split('\n');
        for (const line of lines.slice(0, 10)) {
          const trimmed = line.trim();
          if (trimmed.length > 10 && trimmed.length < 100 && 
              (trimmed.includes('Analysis') || trimmed.includes('Report') || 
               trimmed.includes('Performance') || trimmed.includes('Dashboard'))) {
            return trimmed;
          }
        }
        
        return fileName;
      };
      
      // Create filename with timestamp
      const now = new Date();
      const dateTimeString = now.toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const reportTitle = extractReportTitle(analysisResult);
      const cleanFileName = reportTitle.replace(/[^a-z0-9\s]/gi, '').replace(/\s+/g, '_');
      link.download = `${cleanFileName}_${dateTimeString}.jpg`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      // Reset export state
      setIsExporting(false);
      setExportProgress('');
      setExportType(null);
      
    } catch (error) {
      console.error('Error generating JPEG image:', error);
      setExportProgress('Error generating image');
      setTimeout(() => {
        setIsExporting(false);
        setExportProgress('');
        setExportType(null);
        alert('There was an error generating the JPEG image. Please try again or contact support.');
      }, 2000);
    }
  };

  const handleExportPowerPoint = async () => {
    if (!reportRef.current) return;
    
    try {
      setIsExporting(true);
      setExportType('powerpoint');
      setExportProgress('Preparing PowerPoint export...');
      
      // Extract title from the analysis result
      const extractReportTitle = (response: string): string => {
        const h1Match = response.match(/^#\s+(.+)$/m);
        if (h1Match && h1Match[1]) {
          return h1Match[1].trim();
        }
        return fileName;
      };

      const reportTitle = extractReportTitle(analysisResult);

      // Get organization settings for branding
      const logoImg = reportRef.current.querySelector('.report-logo') as HTMLImageElement;
      const logoUrl = logoImg?.src;

      // Capture chart images using the same approach as Word export
      setExportProgress('Capturing chart visualizations...');
      
      const chartImages: { title: string; imageData: string }[] = [];
      const chartElements = reportRef.current.querySelectorAll('.visualization-container');
      
      console.log(`Found ${chartElements.length} visualization containers for PowerPoint export`);
      
      if (chartElements.length === 0) {
        console.warn('⚠️ No visualization containers found! Checking for alternative selectors...');
        
        // Try alternative selectors
        const alternativeSelectors = [
          '.chart-container',
          '.inline-visualization',
          '.visualization',
          '.chart',
          '[data-chart]',
          'canvas',
          'svg'
        ];
        
        for (const selector of alternativeSelectors) {
          const elements = reportRef.current.querySelectorAll(selector);
          console.log(`Found ${elements.length} elements with selector "${selector}"`);
        }
        
        // Log the DOM structure to help debug
        console.log('Report DOM structure:', reportRef.current.innerHTML.substring(0, 1000));
      }
      
      for (let i = 0; i < chartElements.length; i++) {
        const element = chartElements[i] as HTMLElement;
        setExportProgress(`Processing chart ${i + 1} of ${chartElements.length}...`);
        
        console.log(`Processing chart ${i + 1}:`, element);
        
        try {
          // Wait for chart to be fully rendered - longer wait for complex charts
          await new Promise(resolve => setTimeout(resolve, 2500));
          
          // Get chart title - check multiple possible locations
          const titleElement = element.querySelector('.visualization-title') || 
                             element.querySelector('.chart-title') ||
                             element.querySelector('h3') ||
                             element.querySelector('h4') ||
                             element.querySelector('[data-chart-title]');
          
          let chartTitle = titleElement?.textContent?.trim() || 
                          element.getAttribute('data-chart-title') ||
                          element.querySelector('canvas')?.getAttribute('aria-label') ||
                          `Chart ${i + 1}`;
          
          console.log(`Chart title: ${chartTitle}`);
          
          // First try to get pre-rendered image from data attribute (most reliable)
          const chartContainer = element.querySelector('.chart-container');
          const preRenderedImage = chartContainer?.getAttribute('data-chart-image');
          
          if (preRenderedImage && preRenderedImage.startsWith('data:image/')) {
            console.log(`Using pre-rendered image for chart ${i + 1}`);
            chartImages.push({
              title: chartTitle,
              imageData: preRenderedImage
            });
            continue;
          }
          
          // Check if chart has actual content (canvas or svg)
          const canvas = element.querySelector('canvas');
          const svg = element.querySelector('svg');
          
          if (!canvas && !svg) {
            console.warn(`No canvas or SVG found in chart ${i + 1}`);
            continue;
          }
          
          // If we have a canvas, try to capture it directly first
          if (canvas && canvas instanceof HTMLCanvasElement) {
            try {
              // Ensure canvas has proper dimensions
              if (canvas.width > 0 && canvas.height > 0) {
                // Add white background to canvas
                const ctx = canvas.getContext('2d');
                if (ctx) {
                  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                  ctx.save();
                  ctx.globalCompositeOperation = 'destination-over';
                  ctx.fillStyle = 'white';
                  ctx.fillRect(0, 0, canvas.width, canvas.height);
                  ctx.restore();
                  
                  const canvasDataUrl = canvas.toDataURL('image/png', 1.0);
                  
                  // Restore original image
                  ctx.putImageData(imageData, 0, 0);
                  
                  if (canvasDataUrl && canvasDataUrl.length > 1000) {
                    console.log(`Captured canvas directly for chart ${i + 1}, data length: ${canvasDataUrl.length}`);
                    chartImages.push({
                      title: chartTitle,
                      imageData: canvasDataUrl
                    });
                    continue;
                  }
                }
              }
            } catch (canvasError) {
              console.warn(`Direct canvas capture failed for chart ${i + 1}:`, canvasError);
            }
          }
          
          // Fallback to html2canvas
          console.log(`Falling back to html2canvas for chart ${i + 1}`);
          const chartCanvas = await html2canvas(element, {
            backgroundColor: '#ffffff',
            scale: 2,
            useCORS: true,
            allowTaint: false,
            height: element.scrollHeight || 400,
            width: element.scrollWidth || 600,
            logging: false, // Reduce logging noise
            ignoreElements: (el) => {
              // Ignore elements that might cause issues
              return el.classList.contains('chart-loading') || 
                     el.classList.contains('chart-error') ||
                     el.tagName === 'BUTTON';
            }
          });
          
          const imageData = chartCanvas.toDataURL('image/png', 1.0);
          console.log(`Captured chart image via html2canvas, data length: ${imageData.length}`);
          
          if (imageData && imageData.length > 1000) {
            chartImages.push({
              title: chartTitle,
              imageData: imageData
            });
          } else {
            console.warn(`Chart ${i + 1} image data too small or empty`);
          }
          
        } catch (chartError) {
          console.error(`Failed to capture chart ${i + 1}:`, chartError);
        }
      }
      
      console.log(`Successfully captured ${chartImages.length} chart images`);

      // Prepare data for backend API
      const requestData = {
        reportTitle: reportTitle,
        analysisContent: analysisResult,
        chartImages: chartImages, // Include captured chart images
        organizationSettings: {
          name: 'Lumina AI',
          logoPath: organizationSettings?.logoUrl,
          powerPointSettings: organizationSettings?.powerPointSettings
        },
        userQuestion: userQuestion // Add user question for subtitle
      };
      
      console.log('📤 Sending PowerPoint request with settings:', {
        hasLogo: !!organizationSettings?.logoUrl,
        hasPowerPointSettings: !!organizationSettings?.powerPointSettings,
        fontFamily: organizationSettings?.powerPointSettings?.fontFamily
      });

      setExportProgress('Generating PowerPoint presentation...');

      // Call backend API to generate PowerPoint
      const API_URL = process.env.REACT_APP_API_URL || '/api';
      const response = await axios.post(`${API_URL}/export/powerpoint`, requestData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        responseType: 'blob' // Important for file downloads
      });

      // Create download link
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Create filename
      const cleanTitle = reportTitle.replace(/[^a-z0-9\s]/gi, '').replace(/\s+/g, '_');
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      link.download = `${cleanTitle}_${timestamp}.pptx`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setExportProgress('PowerPoint presentation downloaded successfully!');
      setTimeout(() => {
        setIsExporting(false);
        setExportProgress('');
        setExportType(null);
      }, 2000);

    } catch (error: any) {
      console.error('Error generating PowerPoint presentation:', error);
      setExportProgress('Error generating presentation');
      setTimeout(() => {
        setIsExporting(false);
        setExportProgress('');
        setExportType(null);
        alert(`There was an error generating the PowerPoint presentation: ${error.response?.data?.message || error.message}`);
      }, 2000);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="report-modal" ref={modalRef} onClick={handleOutsideClick}>
      <div className="report-modal-content" ref={reportRef}>
        <div className="report-modal-header">
          <img 
            src="/LuminaAilogoPNG.png" 
            alt="Lumina AI" 
            className="report-logo-image"
            style={{
              height: '40px',
              width: 'auto',
              objectFit: 'contain',
              padding: '8px',
              border: '1px solid rgba(0, 0, 0, 0.1)',
              borderRadius: '6px',
              background: 'rgba(173, 216, 230, 0.3)',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
            }}
          />
          <button className="report-close-button" onClick={onClose}>×</button>
        </div>
        <div className="report-modal-body">
                        <AIReport 
            responseText={analysisResult} 
            fileName={fileName} 
            isKPIAnalysisHighAccuracy={isKPIAnalysisHighAccuracy} 
            kpiCodes={kpiCodes}
            organizationSettings={organizationSettings}
            userQuestion={userQuestion}
          />
        </div>
        <div className="report-modal-footer">
          <button className="report-modal-button" onClick={onClose} disabled={isExporting}>
            Close
          </button>
          <div className="export-buttons-group">
            {analysisType === 'analysis' ? (
              <>
                <button 
                  className="report-modal-button export-button" 
                  onClick={handleExportWord}
                  disabled={isExporting}
                  title="Download as Word document"
                >
                  {isExporting && exportType === 'word' ? (
                    <span className="export-loading">
                      <span className="spinner-icon"></span>
                      {exportProgress || 'Processing...'}
                    </span>
                  ) : (
                    <>
                      <span className="export-icon">📝</span>
                      Download Word
                    </>
                  )}
                </button>
                <button 
                  className="report-modal-button export-button jpeg-button" 
                  onClick={handleExportJPEG}
                  disabled={isExporting}
                  title="Download as JPEG image"
                >
                  {isExporting && exportType === 'jpeg' ? (
                    <span className="export-loading">
                      <span className="spinner-icon"></span>
                      {exportProgress || 'Processing...'}
                    </span>
                  ) : (
                    <>
                      <span className="export-icon">🖼️</span>
                      Download Image
                    </>
                  )}
                </button>
              </>
            ) : (
              <button 
                className="report-modal-button export-button powerpoint-button" 
                onClick={handleExportPowerPoint}
                disabled={isExporting}
                title="Download as PowerPoint presentation"
              >
                {isExporting && exportType === 'powerpoint' ? (
                  <span className="export-loading">
                    <span className="spinner-icon"></span>
                    {exportProgress || 'Processing...'}
                  </span>
                ) : (
                  <>
                    <span className="export-icon">📊</span>
                    Download PowerPoint
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
      
      {isExporting && (
        <div className="report-export-overlay">
          <div className="export-progress-container">
            <div className="export-progress-spinner"></div>
            <div className="export-progress-text">{exportProgress || 'Processing...'}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportPopup; 